<template>
  <nx-editor
    :app="!modal"
    :has-tree-view="treeViewIsActive"
  >
    <template #title>
      <v-icon
        v-if="!hasScheduledPublication && !publishedUrl"
        class="nx-ui--level-1 nx-negative px-2"
      >
        {{ icon }}
      </v-icon>

      <v-btn
        v-else-if="publishedUrl"
        icon
        flat
        :href="publishedUrl"
        target="_blank"
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">
              link
            </v-icon>
          </template>
          <span>{{ $t('message.common.publicUrl') }}</span>
        </v-tooltip>
      </v-btn>

      <v-tooltip
        v-else
        bottom
      >
        <v-icon
          slot:activator
          class="nx-ui--level-1 nx-negative px-2"
          color="warning"
        >
          alarm
        </v-icon>
        {{ $t('message.metadata.scheduled') }} {{ draft.schedulePublish | formatDate }}
      </v-tooltip>

      <nx-editor-title-breadcrumbs
        :loading="!initialized"
        :title="description | capitalize"
        :breadcrumbs="breadcrumbs"
        :icon="breadcrumbIcon"
        :tab="tab"
      />
    </template>

    <template #metadata>
      <nx-chip
        class="hidden-sm-and-down"
        dark
        default
        @click.capture.stop="copyId"
      >
        ID: {{ id }}
      </nx-chip>

      <v-tooltip
        v-if="creationDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ creationDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.creationDate') }}
      </v-tooltip>

      <v-tooltip
        v-if="modificationDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ modificationDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.modificationDate') }}
      </v-tooltip>

      <v-tooltip
        v-show="hasScheduledPublication"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          dark
          alert
        >
          {{ $t('message.metadata.scheduledPublication') }}
        </nx-chip>
        {{ $t('message.metadata.scheduled') }} {{ draft.schedulePublish | formatDate }}
      </v-tooltip>

      <nx-chip
        v-if="entityPublishedVersionStartPublicationDate"
        class="hidden-sm-and-down"
        dark
        success
      >
        {{ $t('message.metadata.lastPublishedDate') }} {{ entityPublishedVersionStartPublicationDate | formatDate }}
      </nx-chip>

      <v-tooltip
        v-if="publishedDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ publishedDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.publishedDate') }}
      </v-tooltip>

      <v-tooltip
        v-if="finishDate"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          class="hidden-sm-and-down"
          dark
          default
        >
          {{ finishDate | formatDate }}
        </nx-chip>
        {{ $t('message.metadata.finishDate') }}
      </v-tooltip>

      <nx-chip
        v-if="relativeVersionId"
        dark
        default
        class="hidden-sm-and-down"
      >
        Version: {{ relativeVersionId }}
      </nx-chip>

      <v-tooltip
        v-if="stateVersionText"
        bottom
        class="hidden-sm-and-down"
      >
        <nx-chip
          slot="activator"
          dark
          :success="isPublished"
          :alert="!isPublished"
        >
          {{ stateVersionText }}
        </nx-chip>
        <span v-html="$t('message.metadata.stateVersion')" />
      </v-tooltip>
    </template>

    <template #actions>
      <nx-entity-metadata-button
        v-if="!treeViewIsActive"
        :id="id"
        :type="entity"
        color="white"
        tooltip="bottom"
      />
      <nx-entity-editor-actions
        :disabled="!initialized"
        :editor-store-path="storePath"
        :config="config"
        :draft="draft"
        :entity="entity"
      />
    </template>

    <template #extension>
      <nx-entity-editor-tabs
        v-if="hasTreeView"
        :tab="tab"
        @changeTab="updateRoute"
      />
      <v-spacer />
      <slot name="extension" />
      <nx-entity-editor-buttons
        v-if="tab === 'editor'"
        v-show="initialized"
        :loading="loading"
        :editor-store-path="storePath"
        :disabled="disabled"
        :allow-publish="allowPublish"
        :form="form"
        :has-preview="hasPreview"
        @save="save"
        @click:preview="showPreview = !showPreview"
        @publish="publish"
        @validate="validate"
        @clone="cloneCurrentVersion"
        @validate:publish="validatePublish"
      />
    </template>

    <template #body>
      <v-layout
        v-if="tab === 'editor'"
        justify-center
        :class="{ 'mt-5 pt-3': modal }"
      >
        <v-flex
          v-show="showPreview"
          key="preview"
          class="xs6"
        >
          <nx-entity-iframe-preview
            v-if="hasPreview"
            ref="preview"
            :preview-url="previewUrl"
          />
        </v-flex>
        <v-flex
          key="form"
          :class="showPreview ? 'xs6' : 'xs12'"
        >
          <v-progress-linear
            v-show="!initialized"
            height="6"
            indeterminate
            :active="!initialized"
            color="accent"
          />
          <nx-entity-form-generator
            v-if="initialized"
            :id="id"
            ref="form"
            :disabled="isFormDisabled"
            :type="entity"
            :path="storePath"
            :editor-fields-config="fieldsConfig"
            :draft="draft"
            :modal="modal"
            @update:field="updateField"
            @update:form-flags="updateFormFlags"
            @update:form-errors="updateFormErrors"
          />
        </v-flex>
      </v-layout>
      <nx-entity-editor-tree-view
        v-if="treeViewIsActive"
        :id="id"
        :entity="entity"
        :description="description"
      />
    </template>
  </nx-editor>
</template>

<script>
import NxEditor from '@/components/editor/Editor';
import NxEntityFormGenerator from '@/modules/entity/editor/EntityFormGenerator';
import NxEntityEditorButtons from '@/modules/entity/editor/EntityEditorButtons';
import NxEntityEditorActions from '@/modules/entity/editor/EntityEditorActions';
import NxEntityIframePreview from '@/modules/entity/preview/EntityIframePreview';
import NxEntityMetadataButton from '@/modules/entity/metadata/EntityMetadataButton';
import NxEntityEditorTabs from '@/modules/entity/editor/EntityEditorTabs';
import NxEntityEditorTreeView from '@/modules/entity/editor/tree-view/EntityEditorTreeView';
import { formatErrorResponseMessage, SiteMismatchError } from '@/api/interceptors/format-error';
import toast from '@/modules/toast/services';
import uuid from 'uuid/v4';
import Storable from '@/mixins/storable';
import Loadable from '@/mixins/loadable';
import NxChip from '@/components/chips/Chip';
import NxEditorTitleBreadcrumbs from '@/components/editor/EditorTitleBreadcrumbs';
import apiFactory from '@/modules/entity/api';
import isArray from 'lodash/isArray';
import { copyToClipboard } from '@/utils';
import { entityTypeTranslation } from '@/modules/entity/utils';
import store from './store';

export default {
  name: 'NxEntityEditor',
  components: {
    NxChip,
    NxEntityEditorActions,
    NxEntityFormGenerator,
    NxEntityEditorButtons,
    NxEntityMetadataButton,
    NxEditor,
    NxEntityIframePreview,
    NxEditorTitleBreadcrumbs,
    NxEntityEditorTabs,
    NxEntityEditorTreeView,
  },
  mixins: [Storable, Loadable],
  metaInfo() {
    if (this.modal) {
      return undefined;
    }
    return {
      title: this.description,
      // all titles will be injected into this template
      titleTemplate: this.description ? '%s | BCube CMS' : 'BCube CMS',
    };
  },
  provide() {
    return { $getParentEditor: () => this };
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    tab: {
      type: String,
      default: 'editor',
    },
    relativeVersionId: {
      type: Number,
    },
    title: {
      type: String,
    },
    entity: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    allowPublish: {
      type: Boolean,
      default: true,
    },
    modal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {},
      initialized: false,
      showPreview: false,
      breadcrumbs: [],
    };
  },
  computed: {
    urlParamId() {
      return this.$route.params.id;
    },
    storePath() {
      return ['entity', 'editor', uuid()];
    },
    draft() {
      return this.state.draft;
    },
    fieldsConfig() {
      return this.state.fieldsConfig;
    },
    isDirty() {
      return this.getters.isDirty;
    },
    isFormDisabled() {
      return this.disabled || this.hasEverBeenPublished || this.draft.readonly === true;
    },
    hasScheduledPublication() {
      return this.getters.hasScheduledPublication;
    },
    hasEverBeenPublished() {
      return this.draft.hasEverBeenPublished === true;
    },
    hasTreeView() {
      return this.config.hasTreeView === true;
    },
    treeViewIsActive() {
      return this.hasTreeView && (this.tab === 'treeView');
    },
    publishedUrl() {
      return this.getters.publishedUrl;
    },
    hasPreview() {
      return this.getters.hasPreview;
    },
    previewUrl() {
      return this.getters.previewUrl;
    },
    config() {
      return this.$store.getters.getEntityConfig(this.entity);
    },
    description() {
      // If the editor has a title use it
      if (this.title) return this.title;
      // If the entity has a editorTitle field use it
      if (this.draft.editorTitle) return this.draft.editorTitle;
      // If the entity has a name field use it
      if (this.draft.name) return this.draft.name;
      // If the entity has the description field, use it
      if (this.draft.description) return this.draft.description;
      // If the entity has a title field use it
      if (this.draft.title) return this.draft.title;
      // If the entity has a text field use it
      if (this.draft.text) return this.draft.text;
      // Use the configured common description or the entity name
      return this.config.description || entityTypeTranslation(this.entity);
    },
    icon() {
      return this.config.icon || 'bug_report';
    },
    creationDate() {
      return this.draft.creationDate;
    },
    modificationDate() {
      return this.draft.modificationDate;
    },
    publishedDate() {
      return this.draft.publishedDate;
    },
    finishDate() {
      return this.draft.finishDate;
    },
    isDraft() {
      return this.draft.isDraft;
    },
    isPublished() {
      return this.draft.published;
    },
    isScheduled() {
      return this.draft.scheduled;
    },
    entityPublishedVersionStartPublicationDate() {
      return this.draft.entityPublishedVersionStartPublicationDate;
    },
    stateVersionText() {
      if (this.isPublished) {
        return this.$t('message.metadata.state.published');
      }
      if (this.hasEverBeenPublished) {
        return this.$t('message.metadata.state.hasBeenPublished');
      }
      if (this.relativeVersionId) {
        return this.$t('message.metadata.state.draft');
      }
      return null;
    },
    versionId() {
      return this.draft.versionId;
    },
    breadcrumbIcon() {
      return this.config.icon;
    },
  },
  watch: {
    entity() {
      this.init();
    },
    id() {
      this.init();
    },
    relativeVersionId() {
      this.init();
    },
  },
  created() {
    this.$store.registerModule(this.storePath, store);
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.$store.unregisterModule(this.storePath);
  },
  methods: {
    async copyId(event) {
      event.stopPropagation();
      event.preventDefault();
      const copyText = this.urlParamId;
      await copyToClipboard(copyText);
      this.toast.notify('message.list.actions.clipboardCopy');
    },
    async init() {
      try {
        this.initialized = false;
        this.form = {};
        this.breadcrumbs = [];
        await this.dispatch('INIT_EDITOR', {
          id: this.id,
          relativeVersionId: this.relativeVersionId,
          entity: this.entity,
        });
        if (this.isDraft) {
          this.$nextTick(this.validate);
        }
        if (this.config.hasBreadcrumb) {
          const entityApi = apiFactory(this.entity);
          const breadcrumb = await entityApi.breadcrumb(this.id);
          this.breadcrumbs = isArray(breadcrumb) ? breadcrumb : [];
        }
        this.initialized = true;
        await this.$store.dispatch('activity/PUSH_ACTIVITY', {
          entity: this.entity,
          id: this.id,
          title: this.description,
        });
      } catch (err) {
        console.error(err);
        if (err instanceof SiteMismatchError) {
          this.$router.push({
            name: 'siteMismatch',
            params: { ...err.cmsVariables, path: this.$route.path },
          });
          return;
        }
        if (err.response.status === 403) {
          this.$router.push({
            name: 'unauthorized',
            path: this.$route.path,
          });
        } else {
          this.$router.push({
            name: 'notFound',
            path: this.$route.path,
          });
        }
      }
      this.$emit('load');
    },
    updateField(field, value) {
      this.dispatch('UPDATE_FIELDS', { [field]: value });
    },
    updateFormErrors(errors) {
      this.dispatch('UPDATE_FORM_ERRORS', errors);
    },
    async save() {
      this.loadingStart();
      try {
        await this.dispatch('SAVE');
        toast.notify('message.entity.actions.savedSuccess');
        if (this.allowPublish) {
          this.validatePublish();
        }
      } catch (error) {
        if (!error
          || !error.response
          || error.response.status !== 403
        ) {
          console.log(error.response);
          toast.error(formatErrorResponseMessage(error.response.data, 'message.entity.actions.savedFailed'));
        }
      } finally {
        this.loadingFinish();
      }
    },
    async publish() {
      this.loadingStart();
      try {
        await this.dispatch('PUBLISH');
        this.form = {};
        toast.notify('message.entity.actions.publishSuccess');
      } catch (err) {
        console.error(err);
        toast.error(formatErrorResponseMessage(error.response.data, 'message.entity.actions.publishFailed'));
      } finally {
        this.loadingFinish();
      }
    },
    async cloneCurrentVersion() {
      if (!this.relativeVersionId) {
        return;
      }
      this.loadingStart();
      try {
        await this.dispatch('CLONE');
        this.form = {};
        toast.notify('message.entity.actions.clonedSuccess');
      } catch (err) {
        console.error(err);
        toast.error(formatErrorResponseMessage(error.response.data, 'message.entity.actions.clonedFail'));
      } finally {
        this.loadingFinish();
      }
    },
    updateFormFlags(form) {
      this.$set(this, 'form', form);
    },
    validate() {
      if (this.$refs.form) {
        this.$refs.form.validateAndShowErrors();
      }
    },
    validatePublish() {
      if (this.$refs.form) {
        this.$refs.form.validateForPublishAndShowErrors();
      }
    },
    updateRoute(tab) {
      const location = { name: 'entity.editor', params: { id: this.id, entity: this.entity, tab } };
      console.debug('Update route', location);
      this.$router.push(location);
    },
  },
};
</script>

<style lang="stylus" scoped>
.nx-container {
  margin-top: 48px;
}
</style>
